/**
 * Manages pools of pre-mapped WebGPU staging buffers so that acquiring a buffer
 * is synchronous and zero-wait. After a buffer's GPU commands are submitted,
 * `mapAsync` is called and its Promise returns the remapped buffer to the pool.
 */
export class PreMappedWriteBufferPool {
  private readonly device: GPUDevice;
  private readonly byteSize: number;
  private readonly labelPrefix: string;
  private readonly availableBuffers: GPUBuffer[] = [];
  private nextId = 0;

  constructor(
    device: GPUDevice,
    byteSize: number,
    labelPrefix: string,
    initialCount: number,
  ) {
    this.device = device;
    this.byteSize = byteSize;
    this.labelPrefix = labelPrefix;

    for (let i = 0; i < initialCount; i++) {
      this.createAndPreMapBuffer();
    }
  }

  private createAndPreMapBuffer(): void {
    const id = this.nextId++;
    const buffer = this.device.createBuffer({
      label: `${this.labelPrefix}-WriteStagingBuffer-${id}`,
      size: this.byteSize,
      usage: GPUBufferUsage.MAP_WRITE | GPUBufferUsage.COPY_SRC,
    });
    buffer
      .mapAsync(GPUMapMode.WRITE)
      .then(() => {
        this.availableBuffers.push(buffer);
      })
      .catch((err: unknown) => {
        console.error('Failed to pre-map write buffer:', err);
      });
  }

  /**
   * Immediately gets a pre-mapped write buffer, copies `data` into its mapped
   * range, and unmaps it so it is ready for `copyBufferToBuffer`.
   */
  acquireAndWrite(data: ArrayBufferView): GPUBuffer {
    let buffer = this.availableBuffers.pop();
    if (!buffer) {
      const id = this.nextId++;
      buffer = this.device.createBuffer({
        label: `${this.labelPrefix}-WriteStagingBuffer-${id}`,
        size: this.byteSize,
        usage: GPUBufferUsage.MAP_WRITE | GPUBufferUsage.COPY_SRC,
        mappedAtCreation: true,
      });
    }

    const dst = new Uint8Array(buffer.getMappedRange());
    const src = new Uint8Array(data.buffer, data.byteOffset, data.byteLength);
    dst.set(src);
    buffer.unmap();
    return buffer;
  }

  /**
   * Call immediately after `device.queue.submit()` to asynchronously remap
   * the buffer and return it to the pre-mapped list for next time.
   */
  remapAfterSubmit(buffer: GPUBuffer): void {
    buffer
      .mapAsync(GPUMapMode.WRITE)
      .then(() => {
        this.availableBuffers.push(buffer);
      })
      .catch((err: unknown) => {
        console.error('Failed to remap write buffer:', err);
      });
  }
}

/**
 * Manages a pool of pre-mapped `MAP_READ | COPY_DST` buffers for reading back
 * audio generated by the compute shader.
 */
export class PreMappedReadBufferPool {
  private readonly device: GPUDevice;
  private readonly byteSize: number;
  private readonly labelPrefix: string;
  private readonly availableBuffers: GPUBuffer[] = [];
  private nextId = 0;

  constructor(
    device: GPUDevice,
    byteSize: number,
    labelPrefix: string,
    initialCount: number,
  ) {
    this.device = device;
    this.byteSize = byteSize;
    this.labelPrefix = labelPrefix;

    for (let i = 0; i < initialCount; i++) {
      this.createAndPreMapBuffer();
    }
  }

  private createAndPreMapBuffer(): void {
    const id = this.nextId++;
    const buffer = this.device.createBuffer({
      label: `${this.labelPrefix}-ReadStagingBuffer-${id}`,
      size: this.byteSize,
      usage: GPUBufferUsage.MAP_READ | GPUBufferUsage.COPY_DST,
    });
    buffer
      .mapAsync(GPUMapMode.READ)
      .then(() => {
        this.availableBuffers.push(buffer);
      })
      .catch((err: unknown) => {
        console.error('Failed to pre-map read buffer:', err);
      });
  }

  /**
   * Immediately gets a pre-mapped buffer from the pool and unmaps it so the
   * GPU can copy compute shader output into it.
   */
  acquireForCopyDst(): GPUBuffer {
    const buffer = this.availableBuffers.pop();
    if (buffer) {
      buffer.unmap();
      return buffer;
    }

    const id = this.nextId++;
    return this.device.createBuffer({
      label: `${this.labelPrefix}-ReadStagingBuffer-${id}`,
      size: this.byteSize,
      usage: GPUBufferUsage.MAP_READ | GPUBufferUsage.COPY_DST,
    });
  }

  /**
   * Call immediately after `device.queue.submit()`. Uses `mapAsync(GPUMapMode.READ)`
   * to remap the buffer, extracts the generated audio samples, and puts the
   * still-mapped buffer back into the list of pre-mapped buffers for next time.
   */
  remapAndReadAfterSubmit(
    buffer: GPUBuffer,
    onRead: (samples: Float32Array) => void,
  ): void {
    buffer
      .mapAsync(GPUMapMode.READ)
      .then(() => {
        const mapped = new Float32Array(buffer.getMappedRange());
        const copy = new Float32Array(mapped.length);
        copy.set(mapped);
        // Keep the buffer mapped and return it to the pre-mapped list for next time
        this.availableBuffers.push(buffer);
        onRead(copy);
      })
      .catch((err: unknown) => {
        console.error('Failed to remap read buffer:', err);
      });
  }
}
